import { useEffect, useState, useTransition, useRef, useMemo, use } from "react";
import Sidebar from "../../../frontend/DesignSystem/Sidebar";
import "../../../css/Components/Sidebar/Sidebar.css";
import InvestmentCardSmall from "./Components/Cards/Investment/InvestmentCardSmall";
import PensionCardSmall from "./Components/Cards/Pension/PensionCardSmall";
import IncomeCardSmall from "./Components/Cards/Income/IncomeCardSmall";
import PersonalDetailCardSmall from "./Components/Cards/PersonalDetail/PersonalDetailCardSmall";
import "../../../css/CashflowModeller2/CashflowModeller.css";
import CfmTabs from "./Components/Tabs/CfmTabs";
import { useCfmContext } from "./Context/CfmContext";
import ChartTabs from "./Components/Charts/Tabs/ChartTabs";
import PensionCalculations from "./Context/HelperFunctions/PensionCalculations";
import InvestmentCalculations from "./Context/HelperFunctions/InvestmentCalculations";
import IncomeExpenditureCalculations from "./Context/HelperFunctions/IncomeExpenditureCalculations";
import {
  saveCashflowModellerData,
  getCashflowModellerData,
} from "./FirebaseHelpers/FirebaseHelpers";
import { useUserEmail } from "../../../context/UserEmailContext";
import { useUserRole } from "../../../context/UserRoleContext";
import ConfirmationModal from "./Components/ConfirmationModal/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import isEqual from "lodash.isequal";
import { pdf } from "@react-pdf/renderer";
import CfmReport from "./Components/Reports/CFMReport";
import { useClientMeta } from "./Hooks/UseClientMeta";
import dayjs from "dayjs";
import pLimit from "p-limit";
import MarketAssumptionsCardSmall from "./Components/Cards/MarketAssumptions/MarketAssumtptionsCardSmall";
import Close from "./Components/Buttons/Close";
import html2canvas from "html2canvas";
import GraphPreRenderer from "./Components/Charts/Utils/GraphPreRenderer";
import { useUserPermissions } from "../../../context/UserPermissionsContext";
import { Helmet } from "react-helmet-async";
import DownloadButton from "./Components/Buttons/DownloadButton";
import SaveButton from "./Components/Buttons/SaveButton";
import AddButton from "./Components/Buttons/AddButton";
import { PDFViewer } from "@react-pdf/renderer";
import { getDownloadURL, ref } from "firebase/storage";
import getAllAdvisers from "../CashflowModeller2/Components/Reports/Test/GetAll";
import { storage } from "../../../backend/firebaseconfig"; // adjust as needed
import { useAdminStatus } from "../../../context/AdminStatusContext";
import { ScenarioBar } from "./Components/ScenarioBar/ScenarioBar";
import { ReactComponent as AddIcon } from "../../../assets/icons/add1.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/trash.svg";
import TooltipBox from "./Components/Tooltips/TooltipBox";
import stripDerivedFields from "./Utils/Clone";
import TooltipBoxRight from "./Components/Tooltips/TooltipBoxRight";
import { useClientActions } from "./Hooks/UseClientActions";
import { ReactComponent as TooltipIcon } from "../../../assets/icons/question-mark-tooltip.svg";
/**
 * CashflowModeller2 is the main component for the Cashflow Modeller tool in the Rosemount Adviser Portal.
 *
 * This component manages the state, calculations, and UI for modelling pensions, investments, and income/expenditure.
 * It handles user permissions, data loading/saving (including Firebase and localStorage), PDF report generation,
 * and navigation with unsaved changes protection.
 *
 * Features:
 * - Loads cached state from localStorage and synchronizes with Firebase.
 * - Tracks unsaved changes and prompts the user before navigating away.
 * - Allows saving the current state to Firebase and localStorage.
 * - Generates a PDF report with chart images and optional tables.
 * - Handles user permissions and redirects unauthorized users.
 * - Renders UI components for personal details, market assumptions, pensions, investments, income, and charts.
 * - Provides modals for confirmation and report download options.
 *
 * @component
 * @returns {JSX.Element} The rendered Cashflow Modeller page.
 */
const CashflowModeller2 = () => {
  PensionCalculations();
  InvestmentCalculations();
  IncomeExpenditureCalculations();

  const { state, dispatch } = useCfmContext();
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { userPermissions } = useUserPermissions();
  const {
    isOwnClient,
    tradingName,
    primaryColor,
    logo,
    userName,
    regulatoryText,
  } = useClientMeta();
  const navigate = useNavigate();
  const [shouldStartRender, setShouldStartRender] = useState(false);
  const [initialising, setInitialising] = useState(true);
  const [activeButton, setActiveButton] = useState("");
  const [isSaved, setIsSaved] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [pensionChartImages, setPensionChartImages] = useState({});
  const [investmentChartImages, setIvestmentChartImages] = useState({});
  const [incExpdChartImages, setIncExpdChartImages] = useState({});
  const [surDefChartImages, setSurDefChartImages] = useState({});
  const [stackedPenChartImages, setStackedPenChartImages] = useState({});
  const [stackedInvChartImages, setStackedInvChartImages] = useState({});
  const [combinedIncExpdChartImages, setCombinedIncExpdChartImages] = useState(
    {}
  );
  const [combinedSurDefChartImages, setCombinedSurDefChartImages] = useState(
    {}
  );
  const { adminStatus } = useAdminStatus();
  const [includeTables, setIncludeTables] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showTablesDialogBox, setShowTablesDialogBox] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [showSavedToast, setShowSavedToast] = useState(false);
  const hideSavedToastRef = useRef(null);
  const [showPdf, setShowPdf] = useState(true);
  const currentAdviserEmail = localStorage.getItem("currentAdviserEmail");
  const adviserStatus = JSON.parse(localStorage.getItem("adviserStatus")) || [];
  const scenarios = state.scenarios || [];
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const rootDocIdRef = useRef(null);
  const getParentId = () => {
    if (state.isScenario && state.parentDocId) return state.parentDocId;
    return rootDocIdRef.current || state.docId || null;
  };
  const baseId = getParentId();
  const effectiveActiveId = activeScenarioId || baseId;
  const { handleDeleteScenario } = useClientActions();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddScenario, setShowAddScenario] = useState(false);
  const [showScenarioTooltip, setShowScenarioTooltip] = useState(false);

  // figure out which scenario pill we’re on: base=1, children start at 2
  const currentScenarioIndex = scenarios.findIndex(
    (s) => s.id === activeScenarioId
  );
  const currentScenarioLabel =
    currentScenarioIndex >= 0 ? currentScenarioIndex + 2 : "";

  useEffect(() => {
    return () => {
      if (hideSavedToastRef.current) clearTimeout(hideSavedToastRef.current);
    };
  }, []);

  // Load cached state
  useEffect(() => {
    const cached = localStorage.getItem("cfmContextData");
    if (cached) {
      try {
        dispatch({ type: "LOAD_ALL", payload: JSON.parse(cached) });
      } catch (err) {
        // console.error("Could not parse cached state", err);
      }
    }
    setTimeout(() => setInitialising(false), 500);
  }, [dispatch]);

  // useEffect(() => {
  //   console.log(regulatoryText);
  // })

  useEffect(() => {
    if (!isOwnClient) return;
    const checkChanges = async () => {
      if (!state.docId) {
        if (state.details?.length) setIsSaved(false);
        return;
      }
      try {
        const firebaseData = await getCashflowModellerData(
          userRole,
          userEmail,
          state.docId
        );
        if (!firebaseData) {
          setIsSaved(false);
          return;
        }
        const {
          pensionResults,
          investmentResults,
          incomeExpenditureResults,
          docId,
          id,
          updatedOn,
          createdAt,
          createdBy,
          tradingName,
          primaryColor,
          logo,
          incomeExpenditureChartImages,
          surpluDeficitChartImages,
          pensionChartImages,
          investmentChartImages,
          combinedIncExpdChartImages,
          combinedSurDefChartImages,
          stackedPenChartImages,
          stackedInvChartImages,
          regulatoryText,
          calculatedIncome,
          isScenario,
          scenarios,

          ...contextData
        } = state;

        const {
          pensionResults: _,
          investmentResults: __,
          incomeExpenditureResults: ___,
          docId: __docId,
          id: __id,
          updatedOn: __updatedOn,
          createdAt: __createdAt,
          createdBy: __createdBy,
          tradingName: _tradingName,
          primaryColor: _primaryColor,
          logo: _logo,
          incomeExpenditureChartImages: _incomeExpenditureChartImages,
          surpluDeficitChartImages: _surpluDeficitChartImages,
          pensionChartImages: _pensionChartImages,
          investmentChartImages: _investmentChartImages,
          combinedIncExpdChartImages: _combinedIncExpdChartImages,
          combinedSurDefChartImages: _combinedSurDefChartImages,
          stackedPenChartImages: _stackedPenChartImages,
          stackedInvChartImages: _stackedInvChartImages,
          regulatoryText: _regulatoryText,
          calculatedIncome: _calculatedIncome,
          isScenario: _isScenario,
          scenarios: _scenarios,

          ...firebaseSanitized
        } = firebaseData;

        setIsSaved(isEqual(contextData, firebaseSanitized));
        // console.log(isSaved, "isSaved");
      } catch {
        setIsSaved(false);
      }
    };
    checkChanges();
  }, [state]);

  /**
   * Handles button click events for the Cashflow Modeller.
   *
   * - Sets the active button state.
   * - If the "Save" button is clicked:
   *   - Extracts relevant data from the current state.
   *   - Checks for an existing document ID; if not present, attempts to retrieve one from Firestore.
   *   - Saves the cashflow modeller data to Firestore, using the existing or new document ID.
   *   - Updates the document ID in the state if it was newly created.
   *   - Stores the relevant context data in localStorage.
   *   - Sets the saved state to true.
   *
   * @async
   * @param {string} buttonText - The text of the button that was clicked.
   * @returns {Promise<void>}
   */
  const handleClick = async (buttonText) => {
    setActiveButton(buttonText);
    if (buttonText !== "Save") return;

    const {
      docId,
      pensionResults,
      investmentResults,
      incomeExpenditureResults,
      logo,
      regulatoryText,
      tradingName,
      primaryColor,
      ...rest
    } = state;

    // if docId exists, update it; if not, create new
    const targetId = docId && docId.trim() !== "" ? docId : null;

    const savedId = await saveCashflowModellerData(
      userRole,
      userEmail,
      rest,
      targetId
    );

    if (savedId !== docId) {
      dispatch({ type: "SET_DOC_ID", payload: savedId });
    }

    localStorage.setItem("cfmContextData", JSON.stringify(rest));
    setIsSaved(true);

    // toast
    setShowSavedToast(true);
    if (hideSavedToastRef.current) clearTimeout(hideSavedToastRef.current);
    hideSavedToastRef.current = setTimeout(
      () => setShowSavedToast(false),
      2000
    );
  };

  const reportInstance = useMemo(
    () => (
      <CfmReport
        state={state}
        tradingName={tradingName}
        primaryColor={primaryColor}
        logo={logo}
        userName={userName}
        regulatoryText={regulatoryText}
        pensionChartImages={pensionChartImages}
        investmentChartImages={investmentChartImages}
        incExpdChartImages={incExpdChartImages}
        surDefChartImages={surDefChartImages}
        stackedPenChartImages={stackedPenChartImages}
        stackedInvChartImages={stackedInvChartImages}
        combinedIncExpdChartImages={combinedIncExpdChartImages}
        combinedSurDefChartImages={combinedSurDefChartImages}
        includeTables={includeTables}
      />
    ),
    [
      state,
      tradingName,
      primaryColor,
      logo,
      userName,
      regulatoryText,
      pensionChartImages,
      investmentChartImages,
      incExpdChartImages,
      surDefChartImages,
      stackedPenChartImages,
      stackedInvChartImages,
      combinedIncExpdChartImages,
      combinedSurDefChartImages,
      includeTables,
    ]
  );

  // useEffect(() => {
  //   console.log(state);
  // }, [state]);

  /**
   * Retrieves a DOM element by its encoded ID.
   *
   * @param {string} selector - The selector string, which can be an ID with or without a leading '#'.
   * @returns {Element|null} The DOM element with the specified ID, or null if not found or if the selector is invalid.
   */
  const getElementByEncodedId = (selector) => {
    if (!selector || typeof selector !== "string") return null;
    const id = selector.startsWith("#") ? selector.slice(1) : selector;
    return document.querySelector(`[id="${id}"]`);
  };

  /**
   * Captures an image (as a PNG data URL) of the DOM element specified by the given selector.
   *
   * @async
   * @function captureImageFromSelector
   * @param {string} selector - The encoded ID or selector used to locate the target DOM element.
   * @returns {Promise<string|null>} A promise that resolves to a PNG data URL of the captured image,
   * or null if the element is not found.
   *
   * @example
   * const imageDataUrl = await captureImageFromSelector('my-element-id');
   */
  const captureImageFromSelector = async (selector) => {
    return new Promise((resolve) => {
      requestAnimationFrame(async () => {
        const el = getElementByEncodedId(selector);
        if (!el) return resolve(null);
        const canvas = await html2canvas(el, {
          backgroundColor: null,
          scale: 3, // keep scale high, but 3 is overkill
          useCORS: true, // allows external images to render faster
        });
        resolve(canvas.toDataURL("image/png"));
      });
    });
  };

  const captureAllCharts = async (state) => {
    const payload = state;
    const hasMultiple = payload.details.length > 1;

    const pensionImages = {};
    const investmentImages = {};
    const incExpdChartImages = {};
    const surDefChartImages = {};
    const stackedPenChartImages = {};
    const stackedInvChartImages = {};
    const combinedIncExpdChartImages = {};
    const combinedSurDefChartImages = {};

    const limit = pLimit(5);

    const chartPromises = payload.details.map((detail, index) =>
      limit(async () => {
        const hasPension = (state.pensions || []).some(
          (p) => p.name === detail.name
        );
        const hasInvestment = (state.investments || []).some(
          (i) => i.name === detail.name
        );

        const selectors = [
          `#sur-def-graph-${encodeURIComponent(detail.name)}`,
          `#sur-def-graph-${encodeURIComponent(detail.name)}`, // (you had this twice; safe to keep/remove)
          `#inc-expd-graph-${encodeURIComponent(detail.name)}`,
          hasPension ? `#graph-${encodeURIComponent(detail.name)}` : null,
          hasInvestment
            ? `#inv-graph-${encodeURIComponent(detail.name)}`
            : null,
        ].filter(Boolean);

        if (hasMultiple && index === 0) {
          selectors.push(
            "#stacked-pen-graph",
            "#stacked-inv-graph",
            "#combined-inc-expd-graph",
            "#combined-sur-def-graph"
          );
        }

        const images = {};
        for (const selector of selectors) {
          const image = await captureImageFromSelector(selector);
          if (image) images[selector] = image;
        }

        if (hasPension)
          pensionImages[detail.name] =
            images[`#graph-${encodeURIComponent(detail.name)}`] || null;
        if (hasInvestment)
          investmentImages[detail.name] =
            images[`#inv-graph-${encodeURIComponent(detail.name)}`] || null;

        incExpdChartImages[detail.name] =
          images[`#inc-expd-graph-${encodeURIComponent(detail.name)}`] || null;
        surDefChartImages[detail.name] =
          images[`#sur-def-graph-${encodeURIComponent(detail.name)}`] || null;

        if (hasMultiple && index === 0) {
          stackedPenChartImages[detail.name] =
            images["#stacked-pen-graph"] || null;
          stackedInvChartImages[detail.name] =
            images["#stacked-inv-graph"] || null;
          combinedIncExpdChartImages[detail.name] =
            images["#combined-inc-expd-graph"] || null;
          combinedSurDefChartImages[detail.name] =
            images["#combined-sur-def-graph"] || null;
        }
      })
    );

    await Promise.all(chartPromises);

    return {
      pensionImages,
      investmentImages,
      incExpdChartImages,
      surDefChartImages,
      stackedPenChartImages,
      stackedInvChartImages,
      combinedIncExpdChartImages,
      combinedSurDefChartImages,
    };
  };

  const fetchLogoUrl = async (storage, tradingName) => {
    try {
      const logoRef = ref(storage, `Logos/${tradingName}.jpg`);
      return await getDownloadURL(logoRef);
    } catch {
      return ""; // fallback
    }
  };

  const handleDownloadAll = async (includeTables) => {
    setDownloading(true);
    try {
      // 1) capture charts ONCE
      const images = await captureAllCharts(state);

      // 2) get advisers
      const advisers = await getAllAdvisers(); // returns [{tradingName, regulatoryText, primaryColor, email, ...}, ...]

      // 3) throttle PDF creation & downloads to avoid memory spikes
      const limit = pLimit(2);

      await Promise.all(
        advisers.map((adviser) =>
          limit(async () => {
            const logoUrl = await fetchLogoUrl(storage, adviser.tradingName);

            const report = (
              <CfmReport
                state={state}
                tradingName={
                  adviser.tradingName || "Rosemount Financial Solutions IFA Ltd"
                }
                regulatoryText={
                  adviser.regulatoryText ||
                  "Rosemount Financial Solutions (IFA) Ltd is authorised and regulated by the Financial Conduct Authority (FCA). Entered on the FCA Register (https://register.fca.org.uk/) under reference 535515. Rosemount Financial Solutions (IFA) Ltd is registered in England and Wales, Registered No. 05668905, Registered Address Rosemount House, 2 – 4 Chequers Road, Basingstoke, RG21 7PU."
                }
                primaryColor={adviser.primaryColor || "081e56"}
                logo={logoUrl || null}
                userEmail={adviser.email || "n/a"}
                pensionChartImages={images.pensionImages}
                investmentChartImages={images.investmentImages}
                incExpdChartImages={images.incExpdChartImages}
                surDefChartImages={images.surDefChartImages}
                stackedPenChartImages={images.stackedPenChartImages}
                stackedInvChartImages={images.stackedInvChartImages}
                combinedIncExpdChartImages={images.combinedIncExpdChartImages}
                combinedSurDefChartImages={images.combinedSurDefChartImages}
                includeTables={includeTables}
              />
            );

            const blob = await pdf(report).toBlob();

            // filename helper
            const safe = (s) => (s || "Report").replace(/[\/\\?%*:|"<>]/g, "_");
            const fileName = `${safe(adviser.tradingName)}.pdf`;

            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          })
        )
      );
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Handles the process of generating and downloading a PDF report of the Cashflow Modeller.
   * Captures chart images from the DOM, prepares the report component with all necessary data and images,
   * generates a PDF blob, and triggers a download with a formatted filename.
   *
   * @async
   * @function
   * @param {boolean} includeTables - Determines whether to include tables in the generated PDF report.
   * @returns {Promise<void>} Resolves when the PDF has been generated and the download has started.
   *
   * @throws {Error} If any error occurs during the image capture or PDF generation process.
   *
   * @example
   * // Download PDF including tables
   * await handleDownloadPDF(true);
   *
   * // Download PDF excluding tables
   * await handleDownloadPDF(false);
   */
  const handleDownloadPDF = async (includeTables) => {
    // setDownloading(true);
    // console.log("Downloading PDF...");

    try {
      const payload = state;
      const hasMultiple = payload.details.length > 1;

      const pensionImages = {};
      const investmentImages = {};
      const incExpdChartImages = {};
      const surDefChartImages = {};
      const stackedPenChartImages = {};
      const stackedInvChartImages = {};
      const combinedIncExpdChartImages = {};
      const combinedSurDefChartImages = {};

      const limit = pLimit(5); // Limit concurrency to 4
      // const limit = pLimit(payload.details.length > 2 ? 2 : 1);

      const chartPromises = payload.details.map((detail, index) =>
        // const safeName = encodeURIComponent(detail.name);

        limit(async () => {
          const hasPension = (state.pensions || []).some(
            (p) => p.name === detail.name
          );
          const hasInvestment = (state.investments || []).some(
            (i) => i.name === detail.name
          );

          const selectors = [
            `#sur-def-graph-${encodeURIComponent(detail.name)}`,
            `#sur-def-graph-${encodeURIComponent(detail.name)}`,
            `#inc-expd-graph-${encodeURIComponent(detail.name)}`,
            hasPension ? `#graph-${encodeURIComponent(detail.name)}` : null,
            hasInvestment
              ? `#inv-graph-${encodeURIComponent(detail.name)}`
              : null,
          ].filter(Boolean);

          // console.log(
          //   "Capturing images for:",
          //   detail.name,
          //   "Selectors:",
          //   selectors
          // );

          if (hasMultiple && index === 0) {
            selectors.push(
              "#stacked-pen-graph",
              "#stacked-inv-graph",
              "#combined-inc-expd-graph",
              "#combined-sur-def-graph"
            );
          }

          const images = {};
          for (const selector of selectors) {
            const image = await captureImageFromSelector(selector);
            if (image) images[selector] = image;
          }

          // console.log("Captured images for:", detail.name, "Images:", images);

          if (hasPension)
            pensionImages[detail.name] =
              images[`#graph-${encodeURIComponent(detail.name)}`] || null;
          if (hasInvestment)
            investmentImages[detail.name] =
              images[`#inv-graph-${encodeURIComponent(detail.name)}`] || null;

          incExpdChartImages[detail.name] =
            images[`#inc-expd-graph-${encodeURIComponent(detail.name)}`] ||
            null;
          surDefChartImages[detail.name] =
            images[`#sur-def-graph-${encodeURIComponent(detail.name)}`] || null;

          if (hasMultiple && index === 0) {
            stackedPenChartImages[detail.name] =
              images["#stacked-pen-graph"] || null;
            stackedInvChartImages[detail.name] =
              images["#stacked-inv-graph"] || null;
            combinedIncExpdChartImages[detail.name] =
              images["#combined-inc-expd-graph"] || null;
            combinedSurDefChartImages[detail.name] =
              images["#combined-sur-def-graph"] || null;
          }
        })
      );

      await Promise.all(chartPromises);

      // console.log("All chart images captured");
      // console.log("Pension Chart Images:", pensionImages);
      // console.log("Investment Chart Images:", investmentImages);
      // console.log("Income Expenditure Chart Images:", incExpdChartImages);
      // console.log("Surplus Deficit Chart Images:", surDefChartImages);

      setPensionChartImages(pensionImages);
      setIvestmentChartImages(investmentImages);
      setIncExpdChartImages(incExpdChartImages);
      setSurDefChartImages(surDefChartImages);
      setStackedPenChartImages(stackedPenChartImages);
      setStackedInvChartImages(stackedInvChartImages);
      setCombinedIncExpdChartImages(combinedIncExpdChartImages);
      setCombinedSurDefChartImages(combinedSurDefChartImages);

      const reportInstance = (
        <CfmReport
          state={state}
          tradingName={tradingName}
          primaryColor={primaryColor}
          logo={logo}
          userName={userName}
          userEmail={
            userRole === "Advisers"
              ? userEmail
              : isOwnClient
              ? userEmail
              : userName
          }
          regulatoryText={regulatoryText}
          pensionChartImages={pensionImages}
          investmentChartImages={investmentImages}
          incExpdChartImages={incExpdChartImages}
          surDefChartImages={surDefChartImages}
          stackedPenChartImages={stackedPenChartImages}
          stackedInvChartImages={stackedInvChartImages}
          combinedIncExpdChartImages={combinedIncExpdChartImages}
          combinedSurDefChartImages={combinedSurDefChartImages}
          includeTables={includeTables}
        />
      );

      const blob = await pdf(reportInstance).toBlob();
      const formattedDate = dayjs().format("DD/MM/YYYY");
      const names =
        state.details
          ?.slice(0, 2)
          .map((d) => d.name)
          .filter(Boolean)
          .join(", ") || "Client";

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${names} - ${formattedDate}.pdf`;
      link.click();
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (
      // (userRole === "Staff" &&
      //   (userPermissions?.includes("Compliance") ||
      //     userEmail === "zachary.bawa@rosemountfs.co.uk" ||
      //     // userEmail === "jacko.lam@rosemountfs.co.uk" ||
      //     userEmail === "calum.anderson@rosemountfs.co.uk" ||
      //     userEmail === "callum.wilkinson@rosemountfs.co.uk" ||
      //     userEmail === "shashank.bedade@rosemountfs.co.uk")) ||
      // adviserStatus.includes("CFM") ||
      // adminStatus.includes("NSCFM")
      (userRole === "Staff" &&
        (userPermissions?.includes("Compliance") ||
          userPermissions?.includes("Paraplanning") ||
          userPermissions?.includes("Compliance Support"))) ||
      adviserStatus.includes("FP") ||
      adviserStatus.includes("NAP") ||
      adminStatus.includes("NSFP")
    ) {
    } else navigate("/members-hub");
  }, []);

  useEffect(() => {
    if (downloading) {
      // Let charts pre-render before calling `handleDownloadPDF`
      requestAnimationFrame(() => {
        setTimeout(() => {
          handleDownloadPDF(!includeTables);
        }, 0); // small delay for paint
      });
    }
  }, [downloading]);

  const duplicateClientAndRegisterScenario = async () => {
    try {
      // 0) Resolve true parent id
      const parentId = getParentId();
      if (!parentId) {
        // console.warn("[duplicate] No parentId found");
        return;
      }

      // 1) clone current view as the scenario content
      const base = stripDerivedFields(state);

      // 2) mark clone as a scenario of the parent
      const clonedPayload = {
        ...base,
        isScenario: true,
        parentDocId: parentId,
        scenarios: [], // child never carries parent's list
        docId: null, // don't persist a docId on the child payload
      };

      // 3) create the child doc
      const newDocId = await saveCashflowModellerData(
        userRole,
        userEmail,
        clonedPayload,
        null
      );

      // 4) pull parent, append new scenario, save back to PARENT
      const parentData = await getCashflowModellerData(
        userRole,
        userEmail,
        parentId
      );
      const parentScenarios = Array.isArray(parentData?.scenarios)
        ? parentData.scenarios
        : [];

      const scenarioLabel = `${parentScenarios.length + 1}`;
      const newScenarioEntry = {
        id: newDocId,
        name: scenarioLabel,
        createdAt: dayjs().toISOString(),
        sourceDocId: parentId,
      };

      const updatedParent = stripDerivedFields({
        ...parentData,
        isScenario: false,
        parentDocId: "", // keep clean on parent
        scenarios: [...parentScenarios, newScenarioEntry],
      });
      // never push docId: null to parent
      delete updatedParent.docId;

      await saveCashflowModellerData(
        userRole,
        userEmail,
        updatedParent,
        parentId
      );

      // 5) update UI bar from the source of truth
      await refreshScenarios();

      // 6) jump to the new scenario (optional)
      await loadScenarioById(newDocId);

      setShowSavedToast(true);
      if (hideSavedToastRef.current) clearTimeout(hideSavedToastRef.current);
      hideSavedToastRef.current = setTimeout(
        () => setShowSavedToast(false),
        2000
      );
    } catch (err) {
      // console.error("Duplicate scenario failed:", err);
    }
  };

  // set the parent (base) as default when docId is available
  useEffect(() => {
    if (rootDocIdRef.current && !activeScenarioId) {
      setActiveScenarioId(rootDocIdRef.current);
    }
  }, [activeScenarioId, state.docId]);

  const loadParent = async () => {
    const parentId = rootDocIdRef.current;
    if (!parentId) return;

    const fetched = await getCashflowModellerData(
      state.roleToUse || userRole,
      state.createdBy || userEmail,
      parentId
    );
    if (!fetched) return;

    const merged = {
      ...fetched,
      docId: parentId, // current doc being viewed = parent
    };

    dispatch({ type: "LOAD_ALL", payload: merged });
    localStorage.setItem("cfmContextData", JSON.stringify(merged));
    setActiveScenarioId(parentId);
    setIsSaved(true);
    await refreshScenarios();
  };

  const loadScenarioById = async (scenarioDocId) => {
    if (!scenarioDocId) return;

    // If they clicked Base, load the parent
    if (scenarioDocId === rootDocIdRef.current) {
      await loadParent();
      return;
    }

    const fetched = await getCashflowModellerData(
      state.roleToUse || userRole,
      state.createdBy || userEmail,
      scenarioDocId
    );
    if (!fetched) return;

    const merged = {
      ...fetched,
      docId: scenarioDocId, // current doc being viewed = child
      scenarios: state.scenarios || [], // keep parent’s bar visible
    };

    dispatch({ type: "LOAD_ALL", payload: merged });
    localStorage.setItem("cfmContextData", JSON.stringify(merged));
    setActiveScenarioId(scenarioDocId);
    setIsSaved(true);
    await refreshScenarios();
  };

  const handleScenarioClick = (id) => {
    if (!id) return;

    if (!isSaved) {
      setShowConfirm(true);
      setPendingNavigation({ type: "scenario", id });
      return;
    }
    loadScenarioById(id);
  };

  // When the confirm modal resolves:
  useEffect(() => {
    if (
      !showConfirm &&
      pendingNavigation &&
      pendingNavigation.type === "scenario"
    ) {
      // the modal has just been closed via onSave/onDiscard paths below
      // (we trigger the switch in those callbacks)
    }
  }, [showConfirm, pendingNavigation]);

  // initialise once when the parent loads
  useEffect(() => {
    // only lock root id when we are on the base (not a scenario)
    if (!rootDocIdRef.current && state.docId && !state.isScenario) {
      rootDocIdRef.current = state.docId;
    }
  }, [state.docId, state.isScenario]);

  const refreshScenarios = async () => {
    const parentId = getParentId();
    if (!parentId) return;

    const parentData = await getCashflowModellerData(
      state.roleToUse || userRole,
      state.createdBy || userEmail,
      parentId
    );
    const list = Array.isArray(parentData?.scenarios)
      ? parentData.scenarios
      : [];
    dispatch({ type: "SET_SCENARIOS", payload: list });

    // ensure Base pill is active by default
    if (!activeScenarioId) setActiveScenarioId(parentId);
  };

  useEffect(() => {
    refreshScenarios();
  }, [state.docId, state.isScenario]); // keeps pills in sync when you load/switch

  const confirmOpenRef = useRef(false);

  const openConfirm = (payload) => {
    if (confirmOpenRef.current) return;
    confirmOpenRef.current = true;
    setPendingNavigation(payload);
    setShowConfirm(true);
  };

  const addBtnRef = useRef(null);
  const [showAddTip, setShowAddTip] = useState(false);

  // inside CashflowModeller2 (same file where you already have getParentId, loadParent, refreshScenarios, etc.)
  const deleteActiveScenario = async () => {
    // must be on a scenario, not the base
    if (!state.isScenario || !state.docId) return;

    const scenarioId = state.docId;
    const parentId = getParentId();
    if (!parentId || scenarioId === parentId) return;

    try {
      await handleDeleteScenario(
        { docId: scenarioId, createdBy: state.createdBy || userEmail },
        async () => {
          // remove it from the parent’s scenarios array
          const parentData = await getCashflowModellerData(
            userRole,
            userEmail,
            parentId
          );
          const nextScenarios = Array.isArray(parentData?.scenarios)
            ? parentData.scenarios.filter((s) => s.id !== scenarioId)
            : [];

          const updatedParent = stripDerivedFields({
            ...parentData,
            isScenario: false,
            parentDocId: "",
            scenarios: nextScenarios,
          });
          delete updatedParent.docId; // don't push a docId onto parent payload

          await saveCashflowModellerData(
            userRole,
            userEmail,
            updatedParent,
            parentId
          );

          // UI: go back to Base and refresh pills
          await loadParent();
          await refreshScenarios();
        }
      );
    } catch (err) {
      // console.error("Delete scenario failed:", err);
    }
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Cashflow Modeller</title>
        <meta
          name="description"
          content="Rosemount Financial Solutions (IFA) Ltd - Adviser Portal"
        />
      </Helmet>
      <Sidebar
        isSetExpanded={true}
        shouldNavigate={(route) => {
          if (!isSaved) {
            // setShowConfirm(true);
            // setPendingNavigation(route);
            openConfirm(route);
            return false;
          }
          return true;
        }}
      />
      {state.docId ? (
        <div className="scenario-bar-container">
          <div className="scenario-bar-container-column">
            {isOwnClient === true && (
              // <AddIcon
              //   className="add-icon"
              //   onClick={duplicateClientAndRegisterScenario}
              // />
              <div
                ref={addBtnRef}
                type="button"
                className="icon-btn add-scenario-btn"
                onClick={() => setShowAddScenario(true)}
                // onClick={duplicateClientAndRegisterScenario}
                onMouseEnter={() => {
                  // setShowAddTip(true);
                  //  console.log("Mouse entered add scenario button");
                }}
                // onMouseLeave={() => setShowAddTip(false)}
                // onFocus={() => setShowAddTip(true)}
                // onBlur={() => setShowAddTip(false)}
                style={{
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <AddIcon className="add-icon" />
              </div>
            )}
            <button
              type="button"
              className={`scenario-pill ${
                effectiveActiveId === baseId ? "scenario-pill--active" : ""
              }`}
              onClick={() => handleScenarioClick(baseId)}
              aria-pressed={effectiveActiveId === baseId}
              disabled={!baseId}
            >
              <span className="Content-SemiBold">1</span>
            </button>

            {/* CHILD scenarios */}
            {scenarios.map((scenario, index) => {
              const isActive = scenario.id === activeScenarioId;
              return (
                <button
                  key={scenario.id}
                  type="button"
                  className={`scenario-pill ${
                    isActive ? "scenario-pill--active" : ""
                  }`}
                  onClick={() => handleScenarioClick(scenario.id)}
                  aria-pressed={isActive}
                >
                  <span className="Content-SemiBold">{index + 2}</span>
                </button>
              );
            })}
          </div>
          <div>
            {effectiveActiveId !== baseId &&
              isOwnClient &&
              scenarios.length > 0 && (
                <DeleteIcon
                  className="add-icon"
                  // onClick={deleteActiveScenario}
                  onClick={() => setShowDeleteConfirm(true)}
                />
              )}
          </div>
        </div>
      ) : null}
      <div className="Cashflow-Modeller-Container">
        {initialising ? (
          <div className="Initialising-Overlay">
            <div className="Spinner" />
          </div>
        ) : (
          <>
            <div className="actions-container">
              {isOwnClient && state.details?.length > 0 && (
                <div className="save-btn-wrapper">
                  <SaveButton
                    buttonText="Save"
                    isActive={activeButton === "Save"}
                    onClick={() => {
                      handleClick("Save");
                    }}
                  />
                  {showSavedToast && (
                    <div
                      className="SavedToast"
                      role="status"
                      aria-live="polite"
                    >
                      <div className="Content">Saved Successfully!</div>
                    </div>
                  )}
                </div>
              )}
              <DownloadButton
                buttonText={downloading ? "Generating..." : "Download"}
                isActive={downloading}
                disabled={downloading}
                onClick={() => {
                  !downloading && setShowTablesDialogBox(true);
                  // !downloading && handleDownloadAll(true);
                }}
              />
            </div>
            <div className="Cashflow-Modeller-Row">
              <div className="scrollbar-container">
                <div className="scrollable-widget">
                  <PersonalDetailCardSmall />
                  <MarketAssumptionsCardSmall />
                  <PensionCardSmall />
                  <InvestmentCardSmall />
                  <IncomeCardSmall />
                </div>
              </div>
              <div className="static-container">
                <CfmTabs />
                <ChartTabs />
              </div>
            </div>
          </>
          // </div>
        )}
      </div>
      {showDeleteConfirm && (
        <div className="dialog-overlay">
          <div className="dialog-box " style={{ background: "white" }}>
            <div className="dialog-header">
              <div />
              <div className="dialog-header-buttons">
                <div onClick={() => setShowDeleteConfirm(false)}>
                  <Close />
                </div>
              </div>
            </div>
            <div className="confirm-delete-dialog">
              <div className="Content-SemiBold">
                Are you sure you want to delete Scenario {currentScenarioLabel}?
              </div>

              <div className="dialog-content">
                <div className="Content-Small">
                  This will permanently remove this scenario.
                </div>
                <div className="Content-Small">You can’t undo this action.</div>
              </div>

              <div
                className="dialog-actions"
                style={{
                  display: "flex",
                  gap: "12px",
                  marginTop: "var(--spacingXL)",
                }}
              >
                <AddButton
                  buttonText="Delete"
                  onClick={async () => {
                    await deleteActiveScenario(); // your existing function
                    setShowDeleteConfirm(false);
                  }}
                />
                <AddButton
                  buttonText="Cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddScenario && (
        <div className="dialog-overlay">
          <div
            className="dialog-box "
            style={{ background: "white", maxWidth: "400px" }}
          >
            <div className="dialog-header">
              <div className="dialog-tooltip-row-inputs">
                <div className="Title">What-If Scenarios </div>
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                  }}
                  onMouseEnter={() => setShowScenarioTooltip(true)}
                  onMouseLeave={() => setShowScenarioTooltip(false)}
                >
                  <TooltipIcon />
                  {showScenarioTooltip && (
                    <TooltipBox>
                      <div
                        className="tooltip-content"
                        style={{ maxWidth: "280px" }}
                      >
                        Model different scenarios by duplicating selected models
                        to test changes such as retiring earlier, adjusting
                        contributions, or taking a larger lump sum. This helps
                        you understand how different decisions impact your
                        future cashflow without altering your main plan.
                      </div>
                    </TooltipBox>
                  )}
                </div>
              </div>
              <div className="dialog-header-buttons">
                <div onClick={() => setShowAddScenario(false)}>
                  <Close />
                </div>
              </div>
            </div>
            <div className="confirm-delete-dialog">
              <div className="Content">
                Are you sure you want to create a new scenario based on Scenario{" "}
                {currentScenarioLabel || 1}?
              </div>

              <div
                className="dialog-actions"
                style={{
                  display: "flex",
                  gap: "12px",
                  marginTop: "var(--spacingXL)",
                }}
              >
                <AddButton
                  buttonText="Create"
                  onClick={async () => {
                    await duplicateClientAndRegisterScenario();
                    setShowAddScenario(false);
                  }}
                />
                <AddButton
                  buttonText="Cancel"
                  onClick={() => setShowAddScenario(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirm && (
        <ConfirmationModal
          title="Unsaved changes"
          // onSave={async () => {
          //   await handleClick("Save");
          //   const nav = pendingNavigation;
          //   setShowConfirm(false);
          //   setPendingNavigation(null);
          //   if (nav?.type === "scenario") loadScenarioById(nav.id);
          // }}
          // onDiscard={() => {
          //   const saved = localStorage.getItem("cfmContextData");
          //   if (saved)
          //     dispatch({ type: "LOAD_ALL", payload: JSON.parse(saved) });
          //   setIsSaved(true);
          //   const nav = pendingNavigation;
          //   setShowConfirm(false);
          //   setPendingNavigation(null);
          //   if (nav?.type === "scenario") loadScenarioById(nav.id);
          // }}
          onSave={async () => {
            await handleClick("Save");
            setShowConfirm(false);
            if (pendingNavigation) navigate(pendingNavigation);
          }}
          onDiscard={() => {
            const saved = localStorage.getItem("cfmContextData");
            if (saved)
              dispatch({ type: "LOAD_ALL", payload: JSON.parse(saved) });
            setIsSaved(true);
            setShowConfirm(false);
            const nav = pendingNavigation;
            // if (pendingNavigation) navigate(pendingNavigation);
            if (nav?.type === "scenario") {
              loadScenarioById(nav.id);
            } else {
              navigate(pendingNavigation);
            }
          }}
          onCancel={() => {
            setShowConfirm(false);
            setPendingNavigation(null);
          }}
        />
      )}
      {showAddTip && (
        <TooltipBoxRight anchorRef={addBtnRef}>
          <div className="tooltip-content">Create Scenario</div>
        </TooltipBoxRight>
      )}
      {downloading && <GraphPreRenderer />}
      {downloading && (
        <div className="Generating-Overlay">
          <div className="Download-Spinner" />
        </div>
      )}
      {showTablesDialogBox && !downloading && (
        <div className="dialog-box download-report-dialog-box">
          <div
            className="download-report-header dialog-header"
            style={{ paddingBottom: "var(--spacingXXL)" }}
          >
            <div className="Label">Close</div>
            <div className="dialog-header-buttons">
              <div onClick={() => setShowTablesDialogBox(false)}>
                <Close />
              </div>
            </div>
          </div>
          <div className="download-report-dialog-box-column ">
            <div className="Title">
              Do you want to include tables in the report?
            </div>
            <div className="download-report-dialog-box-row">
              <AddButton
                buttonText="Yes"
                onClick={() => {
                  setShowTablesDialogBox(false);
                  setIncludeTables(false);
                  startTransition(() => {
                    setDownloading(true);
                    // handleDownloadPDF(true);
                  });
                  // setDownloading(true);
                  // handleDownloadPDF(true);
                  // Handle No action
                  // console.log(includeTables, "includeTables");
                }}
              />
              <AddButton
                buttonText="No"
                onClick={() => {
                  setShowTablesDialogBox(false);
                  setIncludeTables(true);
                  startTransition(() => {
                    setDownloading(true);
                    // handleDownloadPDF(false);
                  });
                  // setDownloading(true);
                  // handleDownloadPDF(false);
                  // Handle Yes action
                  // console.log(includeTables, "includeTables");
                }}
              />
            </div>
          </div>
        </div>
      )}
      {/* {showPdf && (
        <div className="PdfOverlay">
          <div className="PdfOverlayHeader">
            <button onClick={() => setShowPdf(false)}>Close</button>
            <button
              onClick={async () => {
                const blob = await pdf(reportInstance).toBlob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${dayjs().format("DD-MM-YYYY")} Report.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download
            </button>
          </div>
          <PDFViewer width="100%" height="100%">
            {reportInstance}
          </PDFViewer>
        </div>
      )} */}
    </div>
  );
};

export default CashflowModeller2;
